import { Component, Input, Output, EventEmitter, forwardRef, OnInit, OnDestroy } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormControl } from '@angular/forms';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-common-checkbox',
  templateUrl: './checkbox.component.html',
  styleUrls: ['./checkbox.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CheckboxComponent),
      multi: true
    }
  ]
})
export class CheckboxComponent implements ControlValueAccessor, OnInit, OnDestroy {
  @Input() labelKey: string = '';
  /** Parámetros para `labelKey | translate` (p. ej. `{{day}}` en i18n). */
  @Input() labelParams?: Record<string, string | number>;
  /**
   * Variantes DS (Figma "check" component set):
   * - `bullet`: selección única visual (se comporta como checkbox, pero con marker circular).
   * - `check_box`: checkbox cuadrado.
   * - `check_circle`: checkbox circular.
   */
  @Input() type: 'bullet' | 'check_box' | 'check_circle' = 'check_box';
  @Input() disabled?: boolean;
  @Input() labelPosition: 'before' | 'after' = 'after';
  @Input() required: boolean = false;
  @Input() indeterminate: boolean = false;
  @Input() customClass: string = '';
  @Input() id: string = '';
  @Input() name: string = '';
  @Input() formControl: FormControl | null = null;
  /**
   * Solo aplica cuando `type === 'bullet'`.
   * En Munily el bullet debe poder marcarse y desmarcarse (comportamiento tipo checkbox),
   * aunque visualmente use `mat-radio-button` para aprovechar su anatomía.
   */
  @Input() bulletAllowUnselect = true;

  @Output() change = new EventEmitter<boolean>();

  private _value: boolean = false;
  private _onChange = (value: boolean) => { };
  private _onTouched = () => { };
  private formControlSubscription: Subscription | null = null;

  get value(): boolean {
    // Si hay un FormControl, usar su valor, sino usar el valor interno
    if (this.formControl) {
      return this.formControl.value || false;
    }
    return this._value;
  }

  set value(val: boolean) {
    this._value = val;
    // Si hay un FormControl, actualizar su valor
    if (this.formControl) {
      this.formControl.setValue(val, { emitEvent: false }); // Add emitEvent: false here
    } else {
      this._onChange(val);
    }
    this._onTouched();
  }

  ngOnInit(): void {
    this.setupFormControlSubscription();
  }

  ngOnDestroy(): void {
    if (this.formControlSubscription) {
      this.formControlSubscription.unsubscribe();
    }
  }

  private setupFormControlSubscription(): void {
    if (this.formControl) {
      // Sincronizar valor inicial
      this._value = this.formControl.value || false;

      // Suscribirse a cambios del FormControl
      this.formControlSubscription = this.formControl.valueChanges.subscribe(value => {
        this._value = value || false;
        // Remove this line to prevent duplicate emissions: this.change.emit(this._value);
      });

      // Sincronizar estado disabled
      if (this.formControl.disabled) {
        this.disabled = true;
      }
    }
  }

  writeValue(value: boolean): void {
    this._value = value || false;
    if (this.formControl && this.formControl.value !== value) {
      this.formControl.setValue(value, { emitEvent: false });
    }
  }

  registerOnChange(fn: (value: boolean) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    /**
     * IMPORTANT:
     * Este componente soporta dos patrones:
     * - CVA (Angular forms) usando `formControlName` / `ngModel`
     * - Control externo pasado por `@Input() formControl`
     *
     * Cuando se pasa un FormControl por `@Input`, NO debemos llamarle `disable()/enable()`
     * desde `setDisabledState`, porque Angular invoca `setDisabledState` precisamente
     * como efecto de `disable()/enable()`, lo que puede causar recursión infinita.
     *
     * Por eso aquí solo reflejamos el flag `disabled` en la UI.
     */
  }

  onCheckboxChange(event: any): void {
    const newValue = event.checked;
    this.value = newValue;
    this.change.emit(newValue);

    // Marcar como touched si hay FormControl
    if (this.formControl) {
      this.formControl.markAsTouched();
    }
  }

  onBulletRadioChange(checked: boolean): void {
    // El des-seleccionado se maneja en `onBulletClickToggle`.
    if (!checked) return;
    this.value = true;
    this.change.emit(true);
    this.formControl?.markAsTouched();
  }

  onBulletClickToggle(event: MouseEvent): void {
    // Permitimos toggle (true <-> false) salvo disabled.
    if (this.disabled) {
      return;
    }
    // Evita el comportamiento nativo del radio (que solo selecciona).
    event.preventDefault();
    event.stopPropagation();

    const next = this.bulletAllowUnselect ? !this.value : true;
    this.value = next;
    this.change.emit(next);
    this.formControl?.markAsTouched();
  }
}
